import { LocalStorageKeys } from "@is/constants";
import { LocalStorageService } from "@is/services";

/**
 * The session that completed checkout is dead: the backend has locked its
 * basket, so every later basket write on it is rejected. Retiring it used to
 * be the confirmation screen's ~10s timer alone, which never fires if the
 * tablet sleeps, the app is killed, or the page reloads first — and the start
 * screen would then happily reuse the locked session.
 */

/** The session the device is using right now, if any. */
export const getCurrentSessionId = (): string | null =>
  LocalStorageService.getItem(LocalStorageKeys.SessionId);

/**
 * Record a checkout completion against the SESSION ID it belongs to, rather
 * than a bare flag or "whatever session is current now".
 *
 * The caller passes the id it captured when the checkout was started, because
 * a reset can land between the request and its response: recording the
 * current id then would name a live session, and a record must only ever
 * describe the session that actually checked out.
 */
export const markSessionFinished = (sessionId?: string | null): void => {
  if (!sessionId) {
    return;
  }

  LocalStorageService.setItem(LocalStorageKeys.FinishedSessionId, sessionId);
};

/**
 * Remember which session a QR checkout was handed to the shopper for.
 *
 * The QR url the shopper carries away encodes ONE session, but the websocket
 * that reports the payment is scoped to the device (`deviceId`/`storeId`/
 * `tenantId`), not the session — so a success can land long after the device
 * has moved on to another session. Without this the completion would be
 * attributed to whichever session is current, retiring a live basket.
 */
export const rememberQRCheckoutSession = (sessionId?: string | null): void => {
  if (!sessionId) {
    return;
  }

  LocalStorageService.setItem(LocalStorageKeys.QRCheckoutSessionId, sessionId);
};

/** The session the outstanding QR checkout was minted for, if any. */
export const getQRCheckoutSessionId = (): string | null =>
  LocalStorageService.getItem(LocalStorageKeys.QRCheckoutSessionId);

/** Whether this exact session is the one that completed checkout. */
export const isFinishedSession = (sessionId?: string | null): boolean =>
  !!sessionId &&
  sessionId === LocalStorageService.getItem(LocalStorageKeys.FinishedSessionId);

/** Every path that starts a new session drops the record. */
export const clearFinishedSession = (): void => {
  LocalStorageService.removeItem(LocalStorageKeys.FinishedSessionId);
};

/**
 * A new session also drops any outstanding QR correlation: the QR belonged to
 * the session that just ended, and keeping it would let a late payment for it
 * be answered while the device is on a different session. Cleared rather than
 * kept so that a success arriving with no QR of our own outstanding records
 * NOTHING, instead of naming the live session.
 */
export const clearQRCheckoutSession = (): void => {
  LocalStorageService.removeItem(LocalStorageKeys.QRCheckoutSessionId);
};
